import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styles from './InfoModal.module.css';

// Tags sugeridas para acesso rápido
const TAGS_SUGERIDAS = [
    { id: 'destaque', nome: 'Destaque', cor: 'info' },
    { id: 'novo', nome: 'Novo', cor: 'success' },
    { id: 'promocao', nome: 'Promoção', cor: 'warning' },
    { id: 'recorrente', nome: 'Recorrente', cor: 'primary' },
    { id: 'nao_comprar', nome: 'Não Comprar', cor: 'danger' },
    { id: 'baixa_rotacao', nome: 'Analise', cor: 'secondary' },
    { id: 'item_apoio', nome: 'Item de Apoio', cor: 'brown' },
    { id: 'refazer_foto', nome: 'Refazer Foto', cor: 'warning' },
    { id: 'refazer_preco', nome: 'Refazer Preço', cor: 'purple' },
    { id: 'duplicado', nome: 'Duplicado', cor: 'dark' }
];

const InfoModal = ({ show, onHide, produto, tipo }) => {
    const queryClient = useQueryClient();
    const [tagsAtuais, setTagsAtuais] = useState([]);


    const isEcommerce = tipo === 'ecommerce';
    const codigo = isEcommerce ? produto?.codigo : produto?.codigoInterno;

    // Inicializar tags quando produto muda
    useEffect(() => {
        const raw = produto?.informacao;
        let parsed = [];

        if (raw) {
            if (Array.isArray(raw)) {
                parsed = raw;
            } else if (typeof raw === 'string') {
                try {
                    const jsonParsed = JSON.parse(raw);
                    parsed = Array.isArray(jsonParsed) ? jsonParsed : [raw];
                } catch {
                    parsed = [raw];
                }
            }
        }

        // Remover duplicatas e valores vazios
        setTagsAtuais([...new Set(parsed.filter(Boolean))]);
    }, [produto]);

    // Mutation para salvar
    const salvarMutation = useMutation({
        mutationFn: async (novasTags) => {
            const endpoint = isEcommerce
                ? `/api/produtos/ecommerce/${codigo}/informacao`
                : `/api/produtos/loja-fisica/${codigo}/informacao`;

            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ informacao: novasTags })
            });

            if (!response.ok) {
                throw new Error('Erro ao salvar informação');
            }

            return response.json();
        },
        onSuccess: (data) => {
            console.log('[Tags] ✅ Tags salvas com sucesso:', data);

            // Invalidar caches para refletir mudança na hora
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return key === 'ecommerce-produtos' ||
                        key === 'loja-centro-produtos' ||
                        key === 'loja-caxias-produtos' ||
                        key === 'loja-fisica-produtos' ||
                        key === 'produtos';
                }
            });

            // Forçar refetch
            queryClient.refetchQueries({
                predicate: (query) => {
                    const key = query.queryKey[0];
                    return key === 'ecommerce-produtos' ||
                        key === 'loja-centro-produtos' ||
                        key === 'loja-caxias-produtos' ||
                        key === 'loja-fisica-produtos';
                },
                type: 'active'
            });

            onHide();
        },
        onError: (error) => {
            console.error('[Tags] ❌ Erro ao salvar tags:', error);
            alert('Erro ao salvar tags: ' + error.message);
        }
    });



    const handleToggleSugestao = (sugestao) => {
        const tagNome = sugestao.nome;

        // Check if exists either as ID or Name
        const existe = tagsAtuais.some(t =>
            t.toLowerCase() === tagNome.toLowerCase() ||
            t.toLowerCase() === sugestao.id.toLowerCase()
        );

        if (existe) {
            setTagsAtuais(prev => prev.filter(t =>
                t.toLowerCase() !== tagNome.toLowerCase() &&
                t.toLowerCase() !== sugestao.id.toLowerCase()
            ));
        } else {
            setTagsAtuais([...tagsAtuais, tagNome]);
        }
    };

    const handleRemoveTag = (tagParaRemover) => {
        setTagsAtuais(prev => prev.filter(t => t !== tagParaRemover));
    };

    const handleSalvar = () => {
        salvarMutation.mutate(tagsAtuais);
    };

    // Helper para pegar cor
    const getCorTag = (tag) => {
        const sugestao = TAGS_SUGERIDAS.find(s =>
            s.id.toLowerCase() === tag.toLowerCase() ||
            s.nome.toLowerCase() === tag.toLowerCase()
        );
        return sugestao ? sugestao.cor : 'secondary';
    };

    // Helper para styles customizados (cores não padrão do Bootstrap)
    const getStyleTag = (cor) => {
        if (cor === 'brown') return { backgroundColor: '#795548', color: 'white' };
        if (cor === 'purple') return { backgroundColor: '#6f42c1', color: 'white' };
        return {};
    };

    if (!produto) return null;

    return (
        <Modal show={show} onHide={onHide} centered size="sm">
            <Modal.Header closeButton className={styles.header}>
                <Modal.Title className={styles.title}>
                    <i className="fas fa-tags me-2"></i>
                    Gerenciar Tags
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.body}>
                <div className={styles.produtoInfo}>
                    <span className={styles.codigo}>#{codigo}</span>
                    <span className={styles.nome}>{isEcommerce ? produto.nome : produto.descricao}</span>
                </div>



                {/* Lista de Tags Atuais */}
                {tagsAtuais.length > 0 && (
                    <div className={styles.tagsAtuais}>
                        <label className={styles.label}>Tags do Produto:</label>
                        <div className={styles.tagsList}>
                            {tagsAtuais.map((tag, index) => {
                                const cor = getCorTag(tag);
                                const customStyle = getStyleTag(cor);
                                // Se for cor customizada, não passa bg prop
                                const bg = (cor === 'brown' || cor === 'purple') ? undefined : cor;

                                return (
                                    <Badge
                                        key={index}
                                        bg={bg}
                                        style={customStyle}
                                        className={styles.tagBadge}
                                    >
                                        {tag}
                                        <button
                                            className={styles.removeBtn}
                                            onClick={() => handleRemoveTag(tag)}
                                            title="Remover tag"
                                        >
                                            ×
                                        </button>
                                    </Badge>
                                );
                            })}
                        </div>
                    </div>
                )}

                <hr />

                {/* Sugestões Rápidas */}
                <div className={styles.tagsDisponiveis}>
                    <label className={styles.label}>Sugestões Rápidas:</label>
                    <div className={styles.tagsGrid}>
                        {TAGS_SUGERIDAS.map((sugestao) => {
                            const isSelected = tagsAtuais.some(t =>
                                t.toLowerCase() === sugestao.nome.toLowerCase() ||
                                t.toLowerCase() === sugestao.id.toLowerCase()
                            );

                            const customStyle = getStyleTag(sugestao.cor);
                            const bg = (sugestao.cor === 'brown' || sugestao.cor === 'purple') ? undefined : sugestao.cor;

                            return (
                                <button
                                    key={sugestao.id}
                                    className={`${styles.tagOption} ${isSelected ? styles.tagSelected : ''}`}
                                    onClick={() => handleToggleSugestao(sugestao)}
                                >
                                    <Badge bg={bg} style={customStyle} className="me-1">{sugestao.nome}</Badge>
                                    {isSelected && <i className="fas fa-check" style={{ fontSize: '0.7rem', color: '#1cc88a' }}></i>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer className={styles.footer}>
                <Button variant="outline-secondary" size="sm" onClick={onHide}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSalvar}
                    disabled={salvarMutation.isPending}
                >
                    {salvarMutation.isPending ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Salvando...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-save me-1"></i>
                            Salvar Alterações
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default InfoModal;
